import { useCallback, useEffect, useRef, useState } from 'react';
import { has, mergeAll } from 'ramda';
import type { Preset } from '../../../types/Api';
import { REFETCH_EAGRID_EVENT, type RefetchEagridCustomEvent } from '../../../utils/refreshDatagrid';
type UsePresetRowCountsParams = {
  visibleItems: Preset[] | undefined;
  rowCountGetter: (dataQueryId: string) => Promise<number | undefined>;
};
type UsePresetRowCountsReturn = {
  presetsRowCount: Record<string, number | undefined>;
};

/**
 * Fetches and caches row counts for visible presets.
 *
 * Only fetches counts for presets whose `dataQueryId` is not yet cached.
 * Re-fetches when visible items change (e.g. after ResizeObserver reflow)
 * or when a REFETCH_EAGRID_EVENT with `refreshPresetCount` option fires.
 */
export const usePresetRowCounts = (params: UsePresetRowCountsParams): UsePresetRowCountsReturn => {
  const [presetsRowCount, setPresetsRowCount] = useState<Record<string, number | undefined>>({});

  // Ref holds latest presetsRowCount so getPresetsCount can check cached keys at call time
  // without capturing stale state in the callback closure.
  const presetsRowCountRef = useRef(presetsRowCount);

  // eslint-disable-next-line no-restricted-syntax -- intentional: no-deps useEffect syncs ref every render
  useEffect(() => {
    presetsRowCountRef.current = presetsRowCount;
  });

  /**
   * @mandatory Used as a dependency in two useEffect hooks below.
   * Without stable identity, both effects would re-run on every render,
   * causing infinite fetch loops. visibleItems and rowCountGetter
   * are the real change signals — identity stabilizes when they don't change.
   */
  // eslint-disable-next-line react-hooks/preserve-manual-memoization -- intentional: stable identity required as useEffect dependency to prevent infinite fetch loop
  const getPresetsCount = useCallback(() => {
    if (params.rowCountGetter && params.visibleItems) {
      const dataQueryIdsToFetch = params.visibleItems?.filter(preset => !has(preset.dataQueryId, presetsRowCountRef.current))?.map(preset => preset.dataQueryId);

      // Guard: when all visible presets already have their counts cached,
      // there is nothing to fetch.  Without this check, Promise.all([])
      // resolves immediately and setPresetsRowCount creates a new object
      // reference (even though no data changed), triggering a re-render
      // that recreates getPresetsCount → useEffect fires again → infinite loop.
      if (dataQueryIdsToFetch.length === 0) {
        return;
      }
      Promise.all(dataQueryIdsToFetch.map(dataQueryId => params.rowCountGetter(dataQueryId))).then(result => {
        setPresetsRowCount(prev => ({
          ...prev,
          ...mergeAll(result.map((count, index) => ({
            [dataQueryIdsToFetch[index]]: count
          })))
        }));
      });
    }
  }, [params.visibleItems, params.rowCountGetter]);

  // eslint-disable-next-line no-restricted-syntax -- intentional: reactive dep with stable identity that re-stabilizes; getPresetsCount (useCallback) gets new identity when visibleItems changes after ResizeObserver fires, intentionally triggering re-fetch of counts for the now-known visible preset set
  useEffect(() => {
    getPresetsCount();
  }, [getPresetsCount]);

  // eslint-disable-next-line no-restricted-syntax -- intentional: window.addEventListener with cleanup; re-fetches preset row counts when REFETCH_EAGRID_EVENT fires with refreshPresetCount option
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const handleRefetchEvent = (event: Event) => {
      const eventDetail = (event as CustomEvent<RefetchEagridCustomEvent>).detail;
      if (eventDetail.options?.refreshPresetCount) {
        getPresetsCount();
      }
    };
    window.addEventListener(REFETCH_EAGRID_EVENT, handleRefetchEvent);
    return () => {
      window.removeEventListener(REFETCH_EAGRID_EVENT, handleRefetchEvent);
    };
  }, [getPresetsCount]);
  return {
    presetsRowCount
  };
};